import { h, JSX } from 'preact';
import classNames from 'classnames';
import { Modal } from './Modal';
import { TrashIcon } from './Icons';
import { TextField, TextFieldProps } from './TextField';
import { Dropdown, DropdownItemProps, DropdownProps, DropdownLabelProps } from './Dropdown';
import { RadioProps } from './Radio';
import { SellableOption } from '../../types/edit-grid';

export interface EditGridModalProps {
    title: string;
    show: boolean;
    titleLabel?: string;
    productLabel?: string;
    selectItemLabel?: string;
    colorLabel?: string;
    closeLabel?: string;
    saveLabel?: string;
    onRemove?: () => void;
    onSave?: () => void;
    toggleModal: () => void;
    titleField?: TextFieldProps;
    productField?: DropdownProps<SellableOption>;
    colorField?: RadioProps;
    isSaveDisabled?: boolean;
}

export const EditGridModal = (props: EditGridModalProps): JSX.Element => (
    <Modal show={props.show} toggleModal={props.toggleModal} title={props.title} hasCloseButton>
        <div class="flex flex-col">
            <div class="flex flex-col py-6 px-8 grow">
                <table class="[&>tr+tr>td]:pt-6">
                    {props.titleField && (
                        <tr>
                            <td class="font-semibold mr-6 w-[90px]">{props.titleLabel}</td>
                            <td>
                                <TextField {...props.titleField} />
                            </td>
                        </tr>
                    )}
                    {props.productField && (
                        <tr>
                            <td className="font-semibold mr-6 pr-4 w-[90px]">{props.productLabel}</td>
                            <td>
                                <Dropdown
                                    {...props.productField}
                                    dropdownLabelElement={(dropdownButtonProps: DropdownLabelProps<SellableOption>) => (
                                        <span>{dropdownButtonProps.value?.name || props.selectItemLabel}</span>
                                    )}
                                    dropdownItemElement={(dropdownElementProps: DropdownItemProps<SellableOption>) => (
                                        <span class="flex text-left w-full py-2.5 px-3">
                                            {dropdownElementProps.value.name}
                                        </span>
                                    )}
                                />
                            </td>
                        </tr>
                    )}
                    {props.colorField && (
                        <tr>
                            <td className="font-semibold mr-6 pr-4 w-[90px]">{props.colorLabel}</td>
                            <td>
                                <fieldset class="-m-2 max-w-[250px]">
                                    {props.colorField.options.map((option) => (
                                        <label class="relative basis-1/5 m-2 inline-block cursor-pointer">
                                            <input
                                                class="opacity-0 sr-only !absolute inset-0"
                                                type="radio"
                                                name={props.colorField.name}
                                                onClick={() => props.colorField.onChange(option.value)}
                                                aria-label={option.label}
                                                checked={props.colorField.value == option.value}
                                            />
                                            <div
                                                class={classNames('btn btn-border p-2', {
                                                    'btn-border': option.value !== props.colorField.value,
                                                    'btn-light-blue': option.value === props.colorField.value,
                                                })}
                                            >
                                                <div
                                                    class={classNames(
                                                        'h-4 w-4 rounded-full border-white border',
                                                        'bg-kinola-complimentary-' + option.value
                                                    )}
                                                />
                                            </div>
                                        </label>
                                    ))}
                                </fieldset>
                            </td>
                        </tr>
                    )}
                </table>
            </div>
            <div class="flex shrink justify-between items-center border-t px-8 py-6">
                {props.onRemove && (
                    <button
                        onClick={props.onRemove}
                        class="btn btn-border p-3 text-kinola-red hover:text-kinola-red-h rounded-2xl"
                    >
                        <TrashIcon />
                    </button>
                )}
                <div class="flex gap-2.5 ml-auto">
                    {props.closeLabel && (
                        <button onClick={props.toggleModal} class="btn btn-border p-4 text-base leading-[14px]">
                            {props.closeLabel}
                        </button>
                    )}
                    {props.saveLabel && (props.onSave || props.isSaveDisabled) && (
                        <button
                            onClick={props.onSave}
                            disabled={props.isSaveDisabled}
                            class={classNames(
                                'btn p-4 text-base leading-[14px]',
                                props.isSaveDisabled ? 'btn-disabled' : 'btn-light-blue'
                            )}
                        >
                            {props.saveLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    </Modal>
);
